import { ProductLine } from '@trucktrax/trucktrax-ts-common';

import {
  ACTIVE_PIN_MAP,
  CLEAR_SELECTED_MARKER,
  CLOSE_ALL_POPUPS,
  CLOSE_POPUP,
  OPEN_POPUP,
  REFRESH_POSITIONS,
  REMOVE_POSITION,
  REMOVE_INCOMING_POSITION,
  SELECT_MARKER,
  SET_ACTIVE_MARKERS,
  SET_IS_PANNED,
  SET_PIN_RADIUS,
  SET_POSITION_LIST,
  SET_INCOMING_POSITION_LIST,
  UPDATE_POSITION,
  UPDATE_INCOMING_POSITION,
} from '../../constants/actionConstants';
import { DEFAULT_RADIUS } from '../../constants/mapConstants';
import { PayloadAction, PositionDetailsDto } from '../../types';
import { isValidLatLng } from '../../util/geoUtil';
import { determineRouteHistoryMarkerLocation } from '../../util/markerUtil';
import { MarkerInfo, PinLocationInfo } from '../actions/mapviewActions';

export type PopupToggleMapValue = {
  isOpen: boolean;
  timestamp: number | null;
  routeHistoryMarker?: boolean;
};

export type ProductLineToggleMap = {
  [key in ProductLine]: { [key: string]: PopupToggleMapValue };
};

export type PopupToggleMap = {
  region: Record<string, Partial<ProductLineToggleMap>> | null;
  lastMarkerSelected: string | null;
};

export type PopupTogglePayload = {
  markerId: string;
  regionId: string;
  productLine: ProductLine;
  routeHistoryMarker?: boolean;
};

export const popupToggleMap = (
  state: PopupToggleMap = { region: null, lastMarkerSelected: null },
  action: PayloadAction<PopupTogglePayload, 'OPEN_POPUP' | 'CLOSE_POPUP' | 'CLOSE_ALL_POPUPS'>,
): PopupToggleMap => {
  switch (action.type) {
    case OPEN_POPUP: {
      const currentRegionState = state.region ?? {};
      const { regionId, markerId, productLine, routeHistoryMarker } = action.payload;

      if (!regionId || !productLine || !markerId) {
        return state;
      }

      const now = Date.now();
      const regionEntry = { ...(currentRegionState[regionId] ?? {}) } as Record<
        string,
        Record<string, PopupToggleMapValue>
      >;
      const productLineEntry = { ...(regionEntry[productLine] ?? {}) };

      if (routeHistoryMarker) {
        Object.keys(productLineEntry).forEach(existingMarkerId => {
          const existing = productLineEntry[existingMarkerId];
          if (existing?.routeHistoryMarker) {
            productLineEntry[existingMarkerId] = {
              ...existing,
              isOpen: false,
              timestamp: null,
            };
          }
        });
      }

      productLineEntry[markerId] = {
        isOpen: true,
        timestamp: now,
        routeHistoryMarker,
      };

      regionEntry[productLine] = productLineEntry;

      return {
        ...state,
        region: {
          ...currentRegionState,
          [regionId]: regionEntry,
        },
        lastMarkerSelected: markerId,
      };
    }
    case CLOSE_POPUP: {
      const currentRegionState = state.region ?? {};
      const { regionId, markerId, productLine } = action.payload;

      if (!regionId || !productLine || !markerId) {
        return state;
      }

      const regionEntry = { ...(currentRegionState[regionId] ?? {}) } as Record<
        string,
        Record<string, PopupToggleMapValue>
      >;
      const productLineEntry = { ...(regionEntry[productLine] ?? {}) };
      if (productLineEntry) {
        productLineEntry[markerId] = {
          isOpen: false,
          timestamp: null,
          routeHistoryMarker: productLineEntry[markerId]?.routeHistoryMarker,
        };
      }
      regionEntry[productLine] = productLineEntry;
      return { ...state, region: { ...currentRegionState, [regionId]: regionEntry } };
    }
    case CLOSE_ALL_POPUPS:
      return { region: null, lastMarkerSelected: null };
    default:
      return state;
  }
};

export const selectedMarker = (
  state: Partial<MarkerInfo> = {
    isPannedTo: false,
    selectedFrom: undefined,
  },
  action: PayloadAction<MarkerInfo, 'SELECT_MARKER' | 'SET_IS_PANNED' | 'CLEAR_SELECTED_MARKER'>,
): Partial<MarkerInfo> => {
  switch (action.type) {
    case SELECT_MARKER: {
      if (!action.payload.routeHistoryMarker) {
        return {
          ...state,
          ...action.payload,
          isPannedTo: false,
        };
      }

      const historicalTruckLocation = determineRouteHistoryMarkerLocation(action.payload);
      const canUseHistoricalTruckLocation =
        !historicalTruckLocation ||
        !isValidLatLng(historicalTruckLocation.lat, historicalTruckLocation.lng);

      return {
        ...state,
        ...action.payload,
        ...(canUseHistoricalTruckLocation ? historicalTruckLocation : {}),
        isPannedTo: false,
      };
    }
    case SET_IS_PANNED:
      return {
        ...state,
        isPannedTo: true,
      };
    case CLEAR_SELECTED_MARKER:
      return {
        isPannedTo: false,
      };
    default:
      return state;
  }
};

export const activeMarkers = (
  state: any = [],
  action: PayloadAction<any[], 'SET_ACTIVE_MARKERS'>,
): any => {
  switch (action.type) {
    case SET_ACTIVE_MARKERS:
      return action.payload;
    default:
      return state;
  }
};

/**
 * Two positions refer to the same truck/driver when either the position record
 * id matches or, more robustly, when they share a tracked object URL (or driver
 * URL for GT-Lite drivers without a truck). The backend can issue a new
 * position record id for follow-up SignalR pings, so matching solely by `id`
 * lets the seeded position and the live ping coexist as separate entries —
 * which leaves the original marker frozen at its initial coordinates because
 * downstream selectors keep the earliest-seen marker per truck. Matching by
 * tracked object keeps a single entry per truck and lets new pings update it.
 */
function isSamePositionTarget(a: PositionDetailsDto, b: PositionDetailsDto): boolean {
  if (a.id != null && b.id != null && a.id === b.id) {
    return true;
  }
  const aTracked = a.trackedObject?.url ?? a.driver?.url;
  const bTracked = b.trackedObject?.url ?? b.driver?.url;
  return !!aTracked && aTracked === bTracked;
}

export const positionList = (
  state: PositionDetailsDto[] = [],
  action: PayloadAction<PositionDetailsDto | PositionDetailsDto[] | number>,
): PositionDetailsDto[] => {
  switch (action.type) {
    case SET_POSITION_LIST: {
      const positions = action.payload as PositionDetailsDto[];
      return positions.filter(position => !!position.driver);
    }
    case UPDATE_POSITION: {
      const positionPayload = action.payload as PositionDetailsDto;

      if (positionPayload.active === false) {
        return state.filter(position => !isSamePositionTarget(position, positionPayload));
      }

      const hasPosition = state.some(position => isSamePositionTarget(position, positionPayload));
      if (hasPosition) {
        return state.map(position => {
          if (isSamePositionTarget(position, positionPayload)) {
            return {
              ...position,
              ...positionPayload,
              ticket: positionPayload.ticket?.closed === true ? undefined : positionPayload.ticket,
              trackedObject: {
                ...position.trackedObject,
                ...(positionPayload.trackedObject ?? positionPayload.driver),
              },
            };
          }
          return position;
        });
      }

      // Match SET_POSITION_LIST: a brand-new position with no driver should
      // never appear on the map. The socket can still emit driverless updates
      // for trucks already in state (those keep flowing through the merge
      // branch above so we don't blow away existing data).
      if (!positionPayload.driver) {
        return state;
      }
      return [...state, positionPayload];
    }
    case REFRESH_POSITIONS:
      return [...state];
    case REMOVE_POSITION: {
      const newState = state.filter(position => position.id !== action.payload);
      return newState;
    }
    default:
      return state;
  }
};

export const incomingPositionList = (
  state: PositionDetailsDto[] = [],
  action: PayloadAction<PositionDetailsDto | PositionDetailsDto[] | number>,
): PositionDetailsDto[] => {
  switch (action.type) {
    case SET_INCOMING_POSITION_LIST: {
      const positions = action.payload as PositionDetailsDto[];
      return positions.filter(position => !!position.driver);
    }
    case UPDATE_INCOMING_POSITION: {
      const positionPayload = action.payload as PositionDetailsDto;

      if (positionPayload.active === false) {
        return state.filter(position => !isSamePositionTarget(position, positionPayload));
      }

      const hasPosition = state.some(position => isSamePositionTarget(position, positionPayload));
      if (hasPosition) {
        return state.map(position => {
          if (isSamePositionTarget(position, positionPayload)) {
            return {
              ...position,
              ...positionPayload,
              ticket: positionPayload.ticket?.closed === true ? undefined : positionPayload.ticket,
              // Preserve the existing trackedObject when the payload does not
              // provide one. The main positionList reducer falls back to the
              // driver here, but the IM selector derives marker identity from
              // ticket.truck.url, so we must not let UPDATE_POSITION turn a
              // null trackedObject into a driver shape.
              trackedObject: positionPayload.trackedObject ?? position.trackedObject,
            };
          }
          return position;
        });
      }

      // New truck reported via socket — accept it. The IM markers selector
      // intersects positions with incoming tickets, so unrelated trucks are
      // filtered out at render time. Match SET_INCOMING_POSITION_LIST and
      // skip driverless new entries.
      if (!positionPayload.driver) {
        return state;
      }
      return [...state, positionPayload];
    }
    case REFRESH_POSITIONS:
      return [...state];
    case REMOVE_INCOMING_POSITION:
      return state.filter(position => position.id !== action.payload);
    default:
      return state;
  }
};

/**
 * Activates the pin map
 * @param {PinLocationInfo} state current state of the pin
 * @param {PayloadAction<PinLocationInfo, 'ACTIVE_PIN_MAP'>} action action with the new values
 * @returns {PinLocationInfo} pin location info response
 */
export const activePinMap = (
  state = {
    pinMode: false,
    pinDropped: false,
    removePin: false,
    location: '',
    lat: undefined,
    lng: undefined,
  } as PinLocationInfo,
  action: PayloadAction<PinLocationInfo, 'ACTIVE_PIN_MAP'>,
) => {
  if (action.type === ACTIVE_PIN_MAP) {
    return action.payload;
  }
  return state;
};

/**
 * Sets the current pin radius
 * @param {string} state current state of the pin radius
 * @param {PayloadAction<string, 'SET_PIN_RADIUS'>} action action with the new values
 * @returns {string} radius response
 */
export const pinRadiusMiles = (
  state = DEFAULT_RADIUS.toString(),
  action: PayloadAction<string, 'SET_PIN_RADIUS'>,
) => {
  if (action.type === SET_PIN_RADIUS) {
    return action.payload as string;
  }
  return state;
};

/**
 * Checks the provided popup toggle map to determine if a given marker is open
 * @param popupMap the current popup toggle map
 * @param regionId a given region's id
 * @param productLine a given product line (Aggregates, ReadyMix, Cement)
 * @param id the id of the marker
 * @returns true when open false when not open or undefined when it isn't found in the popup toggle map
 */
export function isMarkerOpen(
  popupMap: PopupToggleMap,
  regionId?: string,
  productLine?: ProductLine,
  id?: string,
): boolean | undefined {
  const markerInfo = extractMarkerFromPopupToggleMap(popupMap, regionId, productLine, id);
  if (!markerInfo) {
    return undefined;
  }

  return markerInfo.isOpen;
}

/**
 * Checks the provided popup toggle map to determine if a given RouteHistory marker is open
 * @param popupMap the current popup toggle map
 * @param regionId a given region's id
 * @param productLine a given product line (Aggregates, ReadyMix, Cement)
 * @param id the id of the route history marker
 * @returns true when open and the marker is a route history marker
 * false when not open or undefined when it isn't found in the popup toggle map
 */
export function isRouteHistoryMarkerOpen(
  popupMap: PopupToggleMap,
  regionId?: string,
  productLine?: ProductLine,
  id?: string,
): boolean | undefined {
  const markerInfo = extractMarkerFromPopupToggleMap(popupMap, regionId, productLine, id);
  if (!markerInfo) {
    return undefined;
  }

  return markerInfo.isOpen && markerInfo.routeHistoryMarker;
}

/**
 * Extracts the marker from the popup toggle map based on the provided arguments
 * @param popupMap the current popup toggle map
 * @param regionId a given region's id
 * @param productLine a given product line (Aggregates, ReadyMix, Cement)
 * @param id the id of the route history marker
 * @returns the marker when is it found based on the arguments
 * or undefined when it isn't found in the popup toggle map
 */
export function extractMarkerFromPopupToggleMap(
  popupMap: PopupToggleMap,
  regionId?: string,
  productLine?: ProductLine,
  id?: string,
): PopupToggleMapValue | undefined {
  if (!popupMap || !id || !regionId || !productLine) {
    return undefined;
  }
  const region = popupMap.region?.[regionId];
  return region?.[productLine]?.[id];
}
